/**
 * Dashboard aggregation. Pure functions over data already fetched, so every
 * number on the page can be tested without a network or a render.
 *
 * WHAT IS COMPUTABLE, AND FROM WHERE. Worth stating precisely, because the
 * shape of the API decides the shape of the dashboard:
 *
 *   pipeline counts   CandidateItem.status      from the LIST endpoint, cheap
 *   fit distribution  CandidateItem.fitScore    from the LIST endpoint, cheap
 *   recent activity   CandidateItem.lastActivityAt + Job.statusChangedAt
 *   time to hire      Candidate.statusChangedAt from the DETAIL endpoint --
 *                     only affordable because it is fetched for HIRED
 *                     candidates alone, which is a small set
 *   source breakdown  NOT COMPUTABLE. No candidate-sourcing field exists
 *                     anywhere in the API. `source` does appear in the spec
 *                     but belongs to RescoreDimensionChange, which is about
 *                     which model wrote a dimension score. See SOURCE_UNKNOWN.
 *
 * Every aggregate carries the sample it was computed from. A dashboard figure
 * with no denominator invites the reader to assume it covers everything, and
 * these are computed over the jobs we actually fetched.
 */

/**
 * The non-terminal states a candidate can be in, in pipeline order. Paired
 * with TERMINAL_STAGES below, these cover every CandidateStatus value -
 * aggregate.test.ts pins that the two lists together are exhaustive and
 * disjoint, so a status added to one list without the other fails loudly.
 */
export const PIPELINE_STAGES = [
  'NEW',
  'REVIEWED',
  'INVITATION_SENT',
  'RIO_SCREENING',
  'ALT_INTERVIEW',
  'HM_REVIEW',
  'OFFERED',
] as const;

/** Terminal states, which sit outside the pipeline rather than at the end. */
export const TERMINAL_STAGES = ['HIRED', 'REJECTED', 'NOT_INTERESTED'] as const;

export type PipelineStage = (typeof PIPELINE_STAGES)[number];
export type TerminalStage = (typeof TERMINAL_STAGES)[number];
export type CandidateStatus = PipelineStage | TerminalStage;

/**
 * Source breakdown cannot be built. Exported rather than left as a comment so
 * the panel can render an explicit "not available" state that is driven by a
 * value, and so deleting the flag forces the reader to notice.
 */
export const SOURCE_UNKNOWN = true;

/** Minimum candidates before a distribution or an average is worth showing. */
export const MIN_SAMPLE = 5;

export interface JobRow {
  id: number;
  title: string;
  status: string;
  createdAt?: string;
  postedAt?: number | string;
  statusChangedAt?: string;
}

export interface CandidateRow {
  id: number;
  name?: string;
  fitScore?: number | null;
  status: string;
  jobTitle?: string;
  lastActivityAt?: string;
}

/** A HIRED candidate with the detail-only timestamp, fetched per candidate. */
export interface HiredDetail {
  id: number;
  jobId: number;
  statusChangedAt?: string;
}

/** Every aggregate says what it was computed over. */
export interface Sample {
  jobs: number;
  candidates: number;
}

// ---------------------------------------------------------------- pipeline

export interface StageCount {
  stage: CandidateStatus;
  count: number;
  /** Share of the in-pipeline total, 0-1. Terminal stages are excluded. */
  share: number;
}

export interface PipelineSummary {
  openJobs: number;
  closedJobs: number;
  /** In pipeline order. Always all six, including zeros. */
  stages: StageCount[];
  terminal: StageCount[];
  /** Candidates in a non-terminal state. */
  inPipeline: number;
  /** Statuses seen in the data that are not in either list. */
  unrecognised: string[];
  sample: Sample;
}

/**
 * Stage counts from the estate-wide funnel endpoint, in the same shape as
 * summarisePipeline's `stages` -- but not capped by MAX_JOBS_FANNED_OUT,
 * because /api/metrics/funnel is one aggregation query over every match, not
 * a per-job fan-out. Terminal stages are not part of this: the endpoint
 * deliberately excludes them (avrio-backend MetricsService#getFunnelMetrics),
 * so the terminal pill row still reads from the candidate fan-out.
 */
export const stagesFromFunnel = (
  funnel: Record<string, number>,
): { stages: StageCount[]; inPipeline: number } => {
  const inPipeline = PIPELINE_STAGES.reduce((n, s) => n + (funnel[s] ?? 0), 0);
  const stages: StageCount[] = PIPELINE_STAGES.map((stage) => {
    const count = funnel[stage] ?? 0;
    return { stage, count, share: inPipeline > 0 ? count / inPipeline : 0 };
  });
  return { stages, inPipeline };
};

export const summarisePipeline = (
  jobs: JobRow[],
  candidates: CandidateRow[],
): PipelineSummary => {
  const counts = new Map<string, number>();
  candidates.forEach((c) => {
    counts.set(c.status, (counts.get(c.status) ?? 0) + 1);
  });

  const inPipeline = PIPELINE_STAGES.reduce(
    (n, s) => n + (counts.get(s) ?? 0),
    0,
  );

  const stages: StageCount[] = PIPELINE_STAGES.map((stage) => {
    const count = counts.get(stage) ?? 0;
    return { stage, count, share: inPipeline > 0 ? count / inPipeline : 0 };
  });

  const terminal: StageCount[] = TERMINAL_STAGES.map((stage) => ({
    stage,
    count: counts.get(stage) ?? 0,
    share: 0,
  }));

  // A tenth status would otherwise vanish from a page whose whole job is to
  // account for every candidate. Surfaced instead of silently dropped.
  const known = new Set<string>([...PIPELINE_STAGES, ...TERMINAL_STAGES]);
  const unrecognised = Array.from(counts.keys())
    .filter((s) => !known.has(s))
    .sort();

  return {
    openJobs: jobs.filter((j) => j.status === 'OPEN').length,
    closedJobs: jobs.filter((j) => j.status === 'CLOSED').length,
    stages,
    terminal,
    inPipeline,
    unrecognised,
    sample: { jobs: jobs.length, candidates: candidates.length },
  };
};

// ------------------------------------------------------------ fit scores

export interface ScoreBucket {
  /** Inclusive lower bound. */
  from: number;
  /** Exclusive upper bound, except the last bucket which includes 100. */
  to: number;
  count: number;
}

export interface ScoreDistribution {
  buckets: ScoreBucket[];
  /** Candidates carrying a score. */
  scored: number;
  /** Candidates with no score at all -- counted, never treated as zero. */
  unscored: number;
  median: number | null;
  /** True when `scored` is below MIN_SAMPLE, so the UI can decline to plot. */
  tooFewToPlot: boolean;
  sample: Sample;
}

const BUCKET_EDGES = [0, 20, 40, 60, 80, 100] as const;

export const distributeScores = (
  jobs: JobRow[],
  candidates: CandidateRow[],
): ScoreDistribution => {
  // null and undefined are absent, not zero. A candidate with no score is a
  // candidate nobody has scored, and averaging them in as 0 would drag every
  // figure down while looking like a real result.
  const scores = candidates
    .map((c) => c.fitScore)
    .filter((s): s is number => typeof s === 'number' && Number.isFinite(s));

  const buckets: ScoreBucket[] = [];
  for (let i = 0; i < BUCKET_EDGES.length - 1; i += 1) {
    const from = BUCKET_EDGES[i];
    const to = BUCKET_EDGES[i + 1];
    const isLast = i === BUCKET_EDGES.length - 2;
    buckets.push({
      from,
      to,
      count: scores.filter((s) => s >= from && (isLast ? s <= to : s < to))
        .length,
    });
  }

  const sorted = [...scores].sort((a, b) => a - b);
  const median = sorted.length
    ? sorted.length % 2
      ? sorted[(sorted.length - 1) / 2]
      : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
    : null;

  return {
    buckets,
    scored: scores.length,
    unscored: candidates.length - scores.length,
    median,
    tooFewToPlot: scores.length < MIN_SAMPLE,
    sample: { jobs: jobs.length, candidates: candidates.length },
  };
};

// ----------------------------------------------------------- time to hire

export interface TimeToHire {
  /** Days from job creation to the hire's last status change. */
  days: number[];
  medianDays: number | null;
  /** Hires we could not measure, because a timestamp was missing. */
  unmeasurable: number;
  tooFewToReport: boolean;
}

const DAY_MS = 86_400_000;

/**
 * Days between job creation and each hire's status change.
 *
 * Deliberately NOT stage-by-stage dwell time. `statusChangedAt` holds only the
 * latest transition, so intermediate stages cannot be reconstructed -- this is
 * job-open-to-hired and nothing finer. Presenting it as a funnel would be
 * inventing history the data does not contain.
 */
export const measureTimeToHire = (
  jobs: JobRow[],
  hired: HiredDetail[],
): TimeToHire => {
  const jobById = new Map(jobs.map((j) => [j.id, j]));
  const days: number[] = [];
  let unmeasurable = 0;

  hired.forEach((h) => {
    const job = jobById.get(h.jobId);
    const opened = job?.createdAt ? Date.parse(job.createdAt) : NaN;
    const changed = h.statusChangedAt ? Date.parse(h.statusChangedAt) : NaN;
    if (!Number.isFinite(opened) || !Number.isFinite(changed)) {
      unmeasurable += 1;
      return;
    }
    const delta = (changed - opened) / DAY_MS;
    // A negative span means the timestamps disagree about ordering. Counted as
    // unmeasurable rather than clamped to zero, which would quietly pull the
    // median down.
    if (delta < 0) {
      unmeasurable += 1;
      return;
    }
    days.push(Math.round(delta));
  });

  const sorted = [...days].sort((a, b) => a - b);
  const medianDays = sorted.length
    ? sorted.length % 2
      ? sorted[(sorted.length - 1) / 2]
      : Math.round(
          (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2,
        )
    : null;

  return {
    days,
    medianDays,
    unmeasurable,
    tooFewToReport: days.length < 3,
  };
};

// --------------------------------------------------------- recent activity

export interface ActivityEntry {
  kind: 'candidate' | 'job';
  at: string;
  /** Milliseconds, for sorting. */
  atMs: number;
  label: string;
  status: string;
  jobTitle?: string;
}

/**
 * Most recent movement first.
 *
 * Each entry says what something IS now, never what it moved FROM. The API
 * exposes a current status and one timestamp, so "X is now REVIEWED" is
 * supportable and "X moved from NEW to REVIEWED" is not.
 */
export const buildActivityFeed = (
  jobs: JobRow[],
  candidates: CandidateRow[],
  limit = 12,
): ActivityEntry[] => {
  const entries: ActivityEntry[] = [];

  candidates.forEach((c) => {
    if (!c.lastActivityAt) return;
    const atMs = Date.parse(c.lastActivityAt);
    if (!Number.isFinite(atMs)) return;
    entries.push({
      kind: 'candidate',
      at: c.lastActivityAt,
      atMs,
      label: c.name?.trim() || `Candidate ${c.id}`,
      status: c.status,
      jobTitle: c.jobTitle,
    });
  });

  jobs.forEach((j) => {
    if (!j.statusChangedAt) return;
    const atMs = Date.parse(j.statusChangedAt);
    if (!Number.isFinite(atMs)) return;
    entries.push({
      kind: 'job',
      at: j.statusChangedAt,
      atMs,
      label: j.title,
      status: j.status,
    });
  });

  return entries.sort((a, b) => b.atMs - a.atMs).slice(0, limit);
};

export interface JobBreakdownRow {
  jobId: number;
  title: string;
  status: string;
  /** Candidates loaded for this job. */
  total: number;
  /** In a pipeline stage, i.e. not terminal. */
  inPipeline: number;
  /** Waiting on a human: HM_REVIEW. */
  awaitingReview: number;
  hired: number;
  /** Highest fit score among candidates that have one, or null if none do. */
  bestScore: number | null;
  /** How many of `total` carry a score at all. */
  scored: number;
}

/**
 * Per-job counts, for the densest honest use of data already in hand.
 *
 * WHY THIS COSTS NOTHING. The dashboard already issues one candidate request
 * per job, because `GET /jobs` carries no counts. Those responses were being
 * flattened into a single array and the job association thrown away. This uses
 * the grouping that already existed, so it adds no request.
 *
 * WHY IT TAKES `byJob` RATHER THAN THE FLAT LIST. `CandidateItem` has
 * `jobTitle` but no `jobId` -- verified against openapi.yaml -- and titles are
 * not unique, so the flat array genuinely cannot be regrouped afterwards.
 *
 * A job whose request failed is absent from `byJob` and so absent here, rather
 * than appearing with zeros. A row of zeros would read as "this job has no
 * candidates", which is a different claim from "we could not load them".
 * `jobsFailed` on the hook's data carries that count instead.
 *
 * `bestScore` is null rather than 0 when nothing is scored, for the reason
 * distributeScores already documents: an absent score is not a zero score, and
 * rendering one as the other understates the job.
 */
export const summariseByJob = (
  jobs: JobRow[],
  byJob: Array<{ jobId: number; rows: CandidateRow[] }>,
): JobBreakdownRow[] => {
  const titleFor = new Map(jobs.map((j) => [j.id, j]));
  const pipeline = new Set<string>(PIPELINE_STAGES);

  return (
    byJob
      .map(({ jobId, rows }) => {
        const job = titleFor.get(jobId);
        const scores = rows
          .map((r) => r.fitScore)
          .filter((n): n is number => typeof n === 'number');

        return {
          jobId,
          title: job?.title ?? `Job ${jobId}`,
          status: job?.status ?? 'UNKNOWN',
          total: rows.length,
          inPipeline: rows.filter((r) => pipeline.has(r.status)).length,
          awaitingReview: rows.filter((r) => r.status === 'HM_REVIEW').length,
          hired: rows.filter((r) => r.status === 'HIRED').length,
          bestScore: scores.length ? Math.max(...scores) : null,
          scored: scores.length,
        };
      })
      // Most people waiting on a human first: that is the column a hiring team
      // acts on. Ties fall back to pipeline size so the order is deterministic
      // rather than dependent on request completion order.
      .sort(
        (a, b) =>
          b.awaitingReview - a.awaitingReview ||
          b.inPipeline - a.inPipeline ||
          a.jobId - b.jobId,
      )
  );
};
